import React, { FC, useCallback } from 'react';
import {
  RangeDatePicker,
  EngagementDefaultDates,
} from 'Components/RangeDatePicker';
import { FormattedDate } from 'Utils/formattedDate';
import { FilterRadioHidden } from 'Components/FilterRadioHidden';
import Dropdown from 'Components/Dropdown';
import withErrorBoundary from 'HOC/ErrorBoundary';

interface Props {
  callback?: (e: any) => void;
  dateRegistered?: any;
}

const { minDate, maxDate, defaultStartDate, defaultEndDate } =
  EngagementDefaultDates;

const options = [
  // Get this from Redux
  { label: 'Within Last 7 Days', value: 'Within_Last_7_Days' },
  { label: 'Within Last 30 Days', value: 'Within_Last_30_Days' },
  { label: 'Within Last 90 Days', value: 'Within_Last_90_Days' },
  { label: 'Within Last 365 Days', value: 'Within_Last_365_Days' },
];
const overrideStyles = { color: '#079DC0' };
const formatDates = (startDate: string, endDate: string) => {
  const response = { startDate, endDate };
  // Only format is the incoming date strings are not null
  if (startDate !== null) {
    response.startDate = FormattedDate(startDate);
  }
  if (endDate !== null) {
    response.endDate = FormattedDate(endDate);
  }
  return [response.startDate, response.endDate];
};

const colorOverRide = {
  open: {
    border: {
      color: '#B4E1EC',
    },
    text: {
      color: '#079DC0',
    },
  },
  close: {
    border: {
      color: '#B4E1EC',
    },
    text: {
      color: '#079DC0',
    },
  },
};

const DateRegisteredWithMyInstitution: FC<Props> = (props: Props) => {
  const { callback, dateRegistered } = props;

  const handleCalendarChange = (
    selectedStartDate: string,
    selectedChangeEndDate: string
  ) => {
    // Format the dates into the form used for the app
    const [startDate, endDate] = formatDates(
      selectedStartDate,
      selectedChangeEndDate
    );
    const newDate = {
      ...dateRegistered.value,
      lower: startDate,
      upper: endDate,
    };

    // //Send it to be dispatched
    callback({ target: { value: newDate, id: 'Specific Date Range' } });
  };

  const handleDropDownChange = (e: any) => {
    // Find the selected value
    const value = e.target.value.toLocaleLowerCase();
    const selectedOption = options.find(
      (option) => option.value.toLocaleLowerCase() === value
    );
    callback({ target: { value: selectedOption, id: 'Select Recency' } });
  };

  return (
    <>
      <FilterRadioHidden
        label="Any"
        value="Any"
        overrideStyles={overrideStyles}
        selected={dateRegistered.selection === 'Any'}
        identifier="dateRegistered"
        onClick={useCallback(callback, [dateRegistered])}
      />
      <FilterRadioHidden
        label="Select Recency"
        value="Select Recency"
        overrideStyles={overrideStyles}
        selected={dateRegistered.selection === 'Select Recency'}
        identifier="dateRegistered"
        onClick={useCallback(callback, [dateRegistered])}
      >
        <Dropdown
          ariaLabel="Select Recency"
          colorOverRide={colorOverRide}
          items={options}
          defaultValue={
            dateRegistered.selection === 'Select Recency'
              ? dateRegistered.value
              : null
          }
          onChange={useCallback(
            (e: any) => {
              // the onChange event keeps generating a message that is it undefined, if I don't use an anon func instead
              handleDropDownChange(e);
            },
            [dateRegistered]
          )}
        />
      </FilterRadioHidden>
      <FilterRadioHidden
        label="Specific Date Range"
        value="Specific Date Range"
        overrideStyles={overrideStyles}
        selected={dateRegistered.selection === 'Specific Date Range'}
        identifier="dateRegistered"
        onClick={useCallback(callback, [dateRegistered])}
      >
        <RangeDatePicker
          startDate={
            dateRegistered.selection === 'Specific Date Range'
              ? dateRegistered?.value?.lower
              : defaultStartDate
          }
          endDate={
            dateRegistered.selection === 'Specific Date Range'
              ? // Use the upper if it's there, otherwise use the lower
                dateRegistered?.value?.upper
              : defaultEndDate
          }
          minDate={minDate}
          maxDate={maxDate}
          onChange={handleCalendarChange}
          cutOffTop={240}
          usePortals
          closeOnScroll
        />
      </FilterRadioHidden>
    </>
  );
};

DateRegisteredWithMyInstitution.defaultProps = {
  callback: () => {},
  dateRegistered: undefined,
};

export default withErrorBoundary(DateRegisteredWithMyInstitution);
